import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const RepEmailDialog = ({ lead, onClose, team: allTeam, goToTeam, onSent, senderName = "", url, note, submit, allowOther = true }) => {
  const team = allTeam.filter((m) => m.name.trim().toLowerCase() !== senderName.trim().toLowerCase());
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [other, setOther] = useState({ name: "", email: "" });
  const otherTyped = other.name.trim() || other.email.trim();
  const otherOk = other.name.trim() && EMAIL_RX.test(other.email.trim());
  const canSend = (picked.length || otherOk) && (!otherTyped || otherOk);
  const count = picked.length + (otherOk ? 1 : 0);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const close = () => { setPicked([]); setOther({ name: "", email: "" }); onClose(); };

  const send = async () => {
    setBusy(true);
    try {
      const payload = { member_ids: picked, others: otherOk ? [{ name: other.name.trim(), email: other.email.trim() }] : [] };
      const data = submit ? await submit(payload) : (await api.post(url || `/leads/${lead.id}/email-rep`, payload)).data;
      toast.success(`Hot Deal sent to ${data.sent_to.join(", ")}`);
      if (data.potential_of) toast.success(`Now in ${data.potential_of}'s Potential`);
      else if (data.assigned_to) toast.success(`Assigned to ${data.assigned_to}`);
      if (data.failed?.length) toast.warning(`Couldn't reach ${data.failed.join(", ")}`);
      onSent?.();
      close();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md" data-testid="rep-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-blue-700" />Email to rep</DialogTitle>
          <DialogDescription>Sends "Hot Deal from {senderName.split(" ")[0]}" with the complete info for {lead?.name}.{note ? ` ${note}` : ""}</DialogDescription>
        </DialogHeader>
        {!team.length ? (
          <p className="text-sm text-slate-600" data-testid="rep-email-no-team">No reps saved yet. <button className="text-blue-700 underline" onClick={() => { close(); goToTeam(); }}>Add them in the Team tab</button>, or type someone below.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="rep-email-list">
            {team.map((m) => (
              <button key={m.id} onClick={() => toggle(m.id)} data-testid={`rep-email-pick-${m.id}`}
                className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${picked.includes(m.id) ? "!bg-blue-700 border-blue-700 !text-white ring-2 ring-blue-300" : "bg-white border-slate-200 hover:border-blue-300"}`}>
                <span className="block text-sm font-semibold">{m.name}</span>
                <span className={`block text-xs ${picked.includes(m.id) ? "!text-blue-100" : "text-slate-500"}`}>{m.email}</span>
              </button>
            ))}
          </div>
        )}
        {allowOther && <div className="rounded-xl border border-dashed border-slate-300 p-3 space-y-2" data-testid="rep-email-other">
          <p className="text-xs font-semibold text-slate-600">Someone else (not saved to the team)</p>
          <Input placeholder="Name" value={other.name} maxLength={80} onChange={(e) => setOther((o) => ({ ...o, name: e.target.value }))} data-testid="rep-email-other-name" />
          <Input placeholder="Email" type="email" value={other.email} onChange={(e) => setOther((o) => ({ ...o, email: e.target.value }))} data-testid="rep-email-other-email" />
          {otherTyped && !otherOk && <p className="text-xs text-amber-700" data-testid="rep-email-other-hint">Add both a name and a valid email</p>}
        </div>}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={close} data-testid="rep-email-cancel">Cancel</Button>
          <Button disabled={!canSend || busy} onClick={send} className="rounded-full bg-blue-700 hover:bg-blue-800" data-testid="rep-email-send">
            {busy ? "Sending…" : `Send${count > 1 ? ` to ${count}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
